/**
 * Utility functions for condition/filter builders
 * Provides operators and field type detection logic
 */

/**
 * Get available operators based on field data type
 * @param {Object} field - Field object with data_type property
 * @param {Object} options - Options for operator customization
 * @returns {Array} Array of operator objects with {value, label}
 */
export const getOperatorsForField = (field, options = {}) => {
  if (!field) {
    // Default operators when no field is selected
    return [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_null', label: 'Is Null' },
      { value: 'is_not_null', label: 'Is Not Null' }
    ];
  }

  const dataType = (field.data_type || field.dataType || '').toLowerCase();
  const fieldType = (field.field_type || '').toLowerCase();

  // Handle different field naming conventions
  const normalizedType = dataType || fieldType;

  // Check if it's a picklist field
  const isPicklist = normalizedType === 'picklist' || normalizedType === 'multipicklist';

  // Check if it's a boolean/checkbox field
  const isBoolean = normalizedType === 'boolean' || normalizedType === 'checkbox';

  // Check if it's a numeric field (number, integer, decimal, currency, double, int, percent)
  const isNumeric = normalizedType === 'number' || normalizedType === 'integer' ||
                    normalizedType === 'decimal' || normalizedType === 'currency' ||
                    normalizedType === 'double' || normalizedType === 'int' ||
                    normalizedType === 'percent';

  // Check if it's a text field (text, string, email, phone, url, textarea)
  const isText = normalizedType === 'text' || normalizedType === 'string' ||
                 normalizedType === 'email' || normalizedType === 'phone' ||
                 normalizedType === 'url' || normalizedType === 'textarea' ||
                 normalizedType === 'autonumber';

  // If picklist operators are specified, use them
  if (isPicklist && options.picklistOperators) {
    return options.picklistOperators.map(op => {
      if (typeof op === 'string') {
        return { value: op, label: op };
      }
      return op;
    });
  }

  // If custom operators are specified, filter them based on field type
  if (options.customOperators) {
    let operators = options.customOperators.map(op => {
      if (typeof op === 'string') {
        return { value: op, label: op };
      }
      return op;
    });

    // For boolean fields, only allow = and != operators
    if (isBoolean) {
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        // Allow =, !=, == (equality operators only)
        return opValue === '=' || opValue === '!=' || opValue === '==' ||
          opValue === 'equals' || opValue === 'not_equals';
      });
    }

    // For numeric fields, exclude IN, NOT IN, LIKE, and text-specific operators
    if (isNumeric) {
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        const normalizedOp = opValue.toLowerCase().replace(/\s+/g, '_');
        // Exclude IN, NOT IN, LIKE, and text-specific operators for numeric fields
        return normalizedOp !== 'in' && normalizedOp !== 'not_in' &&
               normalizedOp !== 'like' && normalizedOp !== 'contains' &&
               normalizedOp !== 'starts_with' && normalizedOp !== 'ends_with' &&
               opValue !== 'IN' && opValue !== 'NOT IN' && opValue !== 'LIKE' &&
               opValue !== 'Contains' && opValue !== 'Starts With' && opValue !== 'Ends With';
      });
    }

    // For text fields, exclude comparison operators (<, >, <=, >=) and LIKE
    if (isText) {
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        const normalizedOp = opValue.toLowerCase().replace(/\s+/g, '_');
        // Exclude comparison operators and LIKE for text fields
        return opValue !== '<' && opValue !== '>' &&
               opValue !== '<=' && opValue !== '>=' &&
               opValue !== 'LIKE' && normalizedOp !== 'like' &&
               opValue !== 'less_than' && opValue !== 'greater_than' &&
               opValue !== 'less_than_or_equal' && opValue !== 'greater_than_or_equal';
      });
    }

    return operators;
  }

  switch (normalizedType) {
    case 'date':
    case 'datetime':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'greater_than_or_equal', label: 'Greater Than Or Equal' },
        { value: 'less_than_or_equal', label: 'Less Than Or Equal' },
        { value: 'between', label: 'Between' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    case 'number':
    case 'integer':
    case 'decimal':
    case 'currency':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'greater_than_or_equal', label: 'Greater Than Or Equal' },
        { value: 'less_than_or_equal', label: 'Less Than Or Equal' },
        { value: 'between', label: 'Between' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    case 'boolean':
    case 'checkbox':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    case 'picklist':
    case 'multipicklist':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'in', label: 'IN' },
        { value: 'not_in', label: 'NOT IN' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    default:
      // Default for text, email, phone, etc.
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Not Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'like', label: 'LIKE' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];
  }
};

/**
 * Check if operator requires a value
 * @param {string} operator - Operator value
 * @returns {boolean} True if value is required
 */
export const isOperatorValueRequired = (operator) => {
  const valueNotRequiredOperators = ['is_null', 'is_not_null', 'isNull', 'isNotNull', 'isChanged', 'ischanged'];
  return !valueNotRequiredOperators.includes(operator);
};

/**
 * Check if field is a picklist type
 * @param {Object} field - Field object
 * @returns {boolean} True if field is picklist or multipicklist
 */
export const isPicklistField = (field) => {
  if (!field) return false;
  const dataType = (field.data_type || field.dataType || '').toLowerCase();
  return dataType === 'picklist' || dataType === 'multipicklist';
};

/**
 * Check if field is a boolean/checkbox type
 * @param {Object} field - Field object
 * @returns {boolean} True if field is boolean or checkbox
 */
export const isBooleanField = (field) => {
  if (!field) return false;
  const dataType = (field.data_type || field.dataType || '').toLowerCase();
  return dataType === 'boolean' || dataType === 'checkbox';
};

/**
 * Check if field supports multiple values (multipicklist with IN/NOT IN)
 * @param {Object} field - Field object
 * @param {string} operator - Operator value
 * @returns {boolean} True if multiple values are allowed
 */
export const isMultipleValuesAllowed = (field, operator) => {
  if (!isPicklistField(field)) return false;
  if (!operator) return false;
  // Normalize operator for comparison (case-insensitive, handle spaces)
  const normalizedOp = operator.toLowerCase().replace(/\s+/g, '_');
  const multiValueOperators = ['in', 'not_in'];
  return multiValueOperators.includes(normalizedOp);
};

/**
 * Get field by name from fields array
 * @param {Array} fields - Array of field objects
 * @param {string} fieldName - Field name to find
 * @param {Object} options - Options for field matching
 * @returns {Object|null} Field object or null
 */
export const getFieldByName = (fields, fieldName, options = {}) => {
  if (!fields || !fieldName) return null;

  const nameKey = options.nameKey || 'field_name';
  const altNameKey = options.altNameKey || 'name';

  return fields.find(field =>
    field[nameKey] === fieldName || field[altNameKey] === fieldName
  ) || null;
};

/**
 * Get field data type
 * @param {Object} field - Field object
 * @returns {string} Data type (lowercase)
 */
export const getFieldDataType = (field) => {
  if (!field) return 'text';
  return (field.data_type || field.dataType || field.field_type || 'text').toLowerCase();
};
